<template>
  <div id="pdf-viewer">
    <!-- LOADING STATE: Displays spinner and progress percentage -->
    <div v-if="loading" class="flex items-center justify-center w-full py-10">
      <div
        style="border-top-color: transparent"
        class="w-20 h-20 border-4 border-color-link border-solid rounded-full animate-spin"
      ></div>
      <p class="ml-4">{{ loadingProgress }}</p>
    </div>

    <!-- ERROR STATE: Displays error message if PDF fails to load -->
    <div v-if="error" class="text-center py-10 text-red-500">
      {{ error }}
    </div>

    <!--
      PDF RENDERER: vue-pdf-embed component
      Events:
      - @progress - Fires during PDF loading (handled by vue-pdf-embed internally)
      - @loaded - Fires when PDF document loads successfully
      - @loading-failed - Fires on any loading errors
      - @internal-link-clicked - Fires when user clicks TOC or internal PDF links
      - @password-requested - Fires when PDF requires a password (shows warning)
    -->
    <VuePdfEmbed
      v-if="pdfSource && !error"
      :id="`pdf-container`"
      :source="pdfSource"
      :page="pagesToRender"
      :annotation-layer="true"
      :text-layer="true"
      @progress="handleProgress"
      @loaded="handleLoaded"
      @loading-failed="handleLoadingFailed"
      @password-requested="handlePasswordRequested"
      @internal-link-clicked="handleInternalLinkClick"
    />

    <div v-if="hasMorePages && !loading && !error" ref="loadMoreTrigger" class="h-10 text-center py-4">
      Loading more pages...
    </div>
  </div>
</template>

<script>
import VuePdfEmbed from 'vue-pdf-embed'

export default {
  name: 'PdfViewer',
  components: {
    VuePdfEmbed,
  },

  /**
   * PROPS
   * -----
   * @param {String} src - URL or path to PDF file
   */
  props: {
    src: {
      type: String,
      default: '',
    },
  },

  /**
   * EMITTED EVENTS
   * --------------
   * @emits error - When PDF loading fails
   */
  emits: ['error'],

  data() {
    return {
      pdfSource: null,
      numPages: 0,
      renderedPages: 0,
      loading: true,
      loadingProgress: 'Initializing...',
      error: null,
      observer: null,
      isLoadingMore: false,
      loadingTimeout: null,
    }
  },

  computed: {
    /**
     * Checks if there are more pages to load
     * @returns {Boolean} True if not all pages are rendered yet
     */
    hasMorePages() {
      return this.renderedPages < this.numPages
    },

    /**
     * Generates array of page numbers to render
     * @returns {Number[]} Array like [1, 2, 3, ..., renderedPages]
     *
     * Example:
     * - If renderedPages = 5, returns [1, 2, 3, 4, 5]
     * - vue-pdf-embed will render these pages
     */
    pagesToRender() {
      return Array.from({ length: this.renderedPages }, (_, i) => i + 1)
    },
  },

  /**
   * LIFECYCLE HOOKS
   */
  mounted() {
    // Start PDF loading when component is mounted
    this.getPdf()
  },

  beforeUnmount() {
    // Clean up: Disconnect observer to prevent memory leaks
    if (this.observer) {
      this.observer.disconnect()
    }
    // Clear loading timeout if exists
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout)
    }
  },

  methods: {
    /**
     * PROGRESS HANDLER
     * ----------------
     * Handles PDF loading progress from vue-pdf-embed
     * Note: This is called by vue-pdf-embed's internal loading, not our fetch()
     *
     * @param {Object} progressData - Progress data from PDF.js
     * @param {Number} progressData.loaded - Bytes loaded
     * @param {Number} progressData.total - Total bytes
     */
    handleProgress(progressData) {
      console.log('📊 PDF Progress:', progressData)
      if (progressData.loaded && progressData.total) {
        const percent = Math.round((progressData.loaded / progressData.total) * 100)
        this.loadingProgress = `Loading PDF: ${percent}%`
      }
    },

    /**
     * PDF LOADED HANDLER
     * ------------------
     * Called when PDF successfully loads and is ready to render
     *
     * @param {PDFDocumentProxy} pdf - PDF.js document proxy object
     * @param {Number} pdf.numPages - Total number of pages in PDF
     *
     * IMPORTANT: Initial page count is set to 3 for performance.
     * Change Math.min(3, pdf.numPages) to load more/fewer pages initially.
     */
    handleLoaded(pdf) {
      console.log('✅ PDF loaded successfully')

      // Clear loading timeout
      if (this.loadingTimeout) {
        clearTimeout(this.loadingTimeout)
        this.loadingTimeout = null
      }

      this.numPages = pdf.numPages
      // Start by rendering first 3 pages (or fewer if PDF has less than 3 pages)
      this.renderedPages = Math.min(3, pdf.numPages)
      this.loading = false
      this.error = null

      // Wait for DOM update, then setup lazy loading
      this.$nextTick(() => {
        this.setupIntersectionObserver()
      })
    },

    /**
     * PASSWORD REQUESTED HANDLER
     * --------------------------
     * Called when PDF requires a password to open
     * This event is fired by vue-pdf-embed when it detects an encrypted PDF
     * Shows a warning popup since password-protected PDFs are not supported
     */
    handlePasswordRequested() {
      console.log('🔒 Password-Protected PDF detected')

      // Clear loading timeout
      if (this.loadingTimeout) {
        clearTimeout(this.loadingTimeout)
        this.loadingTimeout = null
      }

      // Hide loading spinner and clear pdfSource first
      this.pdfSource = null
      this.loading = false

      // Show warning popup - password-protected PDFs are not supported
      // Use $nextTick to ensure DOM updates after pdfSource is cleared
      this.$nextTick(() => {
        this.error = 'This PDF is password-protected and cannot be viewed.'
        this.$emit('error', { message: this.error })
      })
    },

    /**
     * ERROR HANDLER
     * -------------
     * Called when PDF fails to load or render
     *
     * @param {Error} error - Error object from vue-pdf-embed
     *
     * Common errors:
     * - Network errors (404, 500, CORS issues)
     * - Invalid PDF format
     * - Corrupted PDF data
     * - Password-protected PDFs
     */
    handleLoadingFailed(error) {
      // Clear loading timeout
      if (this.loadingTimeout) {
        clearTimeout(this.loadingTimeout)
        this.loadingTimeout = null
      }

      // Log detailed error information to console for debugging
      console.error('PDF Loading Failed:', {
        message: error.message,
        name: error.name,
        stack: error.stack,
        errorObject: error,
      })

      let userFriendlyError = ''

      // Provide specific error messages based on error type
      if (
        error.message?.includes('password') ||
        error.message?.includes('encrypted') ||
        error.name === 'PasswordException'
      ) {
        console.error('🔒 Password-Protected PDF: This PDF requires a password to open')
        // Show password warning popup
        userFriendlyError = 'This PDF is password-protected and cannot be viewed.'
      } else if (error.message?.includes('Invalid PDF') || error.message?.includes('invalid')) {
        console.error('❌ Invalid PDF Format: The file may not be a valid PDF or is corrupted')
        userFriendlyError = 'Invalid PDF format. The file may not be a valid PDF or is corrupted.'
      } else if (error.message?.includes('corrupted') || error.message?.includes('damaged')) {
        console.error('⚠️ Corrupted PDF: The PDF file appears to be damaged or incomplete')
        userFriendlyError = 'The PDF file appears to be damaged or incomplete.'
      } else if (error.message?.includes('CORS') || error.message?.includes('NetworkError')) {
        console.error('🌐 Network Error: CORS policy or network connectivity issue')
        userFriendlyError = 'Network error: Unable to load PDF due to CORS policy or connectivity issue.'
      } else {
        console.error('📄 PDF Error: Unknown error occurred while loading PDF')
        userFriendlyError = error.message || 'Failed to load PDF'
      }

      this.error = userFriendlyError
      this.loading = false
      this.pdfSource = null
      this.$emit('error', { message: userFriendlyError, originalError: error })
    },

    /**
     * INTERNAL LINK HANDLER
     * ---------------------
     * Handles clicks on PDF internal links (table of contents, cross-references)
     *
     * @param {Number} pageNumber - Target page number from PDF link
     *
     * NOTE: vue-pdf-embed generates page elements with IDs like "page-1", "page-2", etc.
     * We search for these elements and scroll to them.
     *
     * DEBUG: If internal links don't work, check if page elements have correct IDs
     */
    handleInternalLinkClick(pageNumber) {
      const pageElements = document.querySelectorAll('[id^="page-"]')
      const targetPage = Array.from(pageElements).find((el) => el.id === `page-${pageNumber}`)
      if (targetPage) {
        targetPage.scrollIntoView({ behavior: 'smooth' })
      }
    },

    /**
     * PDF FETCHER
     * -----------
     * Fetches PDF from URL and prepares it for vue-pdf-embed
     *
     * PROCESS:
     * 1. Fetch PDF file from URL using Fetch API
     * 2. Read response body as stream for progress tracking
     * 3. Convert chunks to Blob, then ArrayBuffer, then Uint8Array
     * 4. Set pdfSource = { data: Uint8Array }
     * 5. vue-pdf-embed detects pdfSource change and renders PDF
     *
     * WHY UINT8ARRAY?
     * vue-pdf-embed (and PDF.js) requires binary data as Uint8Array
     * This is the most efficient format for PDF.js to process
     *
     * PROGRESS TRACKING:
     * - Uses ReadableStream reader to track download progress
     * - Requires server to send Content-Length header
     * - If Content-Length missing, progress won't show percentage
     *
     * ERROR HANDLING:
     * - Network errors (fetch fails)
     * - HTTP errors (404, 500, etc.)
     * - Processing errors (Blob/ArrayBuffer conversion)
     *
     * @async
     * @throws {Error} If fetch fails or PDF processing fails
     */
    async getPdf() {
      try {
        this.loading = true
        this.loadingProgress = 'Fetching PDF...'
        this.error = null

        // Fetch PDF file
        const response = await fetch(this.src)

        // Check for HTTP errors
        if (!response.ok) {
          throw new Error(`Failed to load PDF: ${response.status} ${response.statusText}`)
        }

        // Get total file size for progress calculation
        const contentLength = response.headers.get('content-length')
        const total = contentLength ? parseInt(contentLength, 10) : 0
        let loaded = 0

        // Read response body as stream (allows progress tracking)
        const reader = response.body.getReader()
        const chunks = []

        // Read all chunks and track progress

        while (true) {
          const { done, value } = await reader.read()
          if (done) {
            break
          }

          chunks.push(value)
          loaded += value.length

          // Update progress percentage (if Content-Length available)
          if (total) {
            const percent = Math.round((loaded / total) * 100)
            this.loadingProgress = `Loading PDF: ${percent}%`
          }
        }

        // Convert chunks to single Uint8Array
        // Step 1: Combine chunks into Blob
        const blob = new Blob(chunks, { type: 'application/pdf' })
        // Step 2: Convert Blob to ArrayBuffer
        const arrayBuffer = await blob.arrayBuffer()
        // Step 3: Convert ArrayBuffer to Uint8Array
        const uint8Array = new Uint8Array(arrayBuffer)

        this.loadingProgress = 'Processing PDF...'

        // Set PDF source for vue-pdf-embed
        // CRITICAL: Must be object with 'data' property containing Uint8Array
        // vue-pdf-embed passes this to PDF.js getDocument() API
        this.pdfSource = { data: uint8Array }

        // Set a timeout to detect if PDF parsing is stuck (e.g., password-protected)
        // If neither handleLoaded, handleLoadingFailed, nor handlePasswordRequested
        // is called within 10 seconds, show an error
        this.loadingTimeout = setTimeout(() => {
          if (this.loading && !this.error) {
            console.error('⏱️ PDF Loading Timeout: PDF parsing took too long')
            this.error = 'Failed to load PDF. The file may be password-protected, corrupted, or too large.'
            this.loading = false
            this.pdfSource = null
            this.$emit('error', new Error('PDF loading timeout'))
          }
        }, 10000)

        // Note: At this point, vue-pdf-embed will detect pdfSource change
        // and trigger PDF rendering. If the PDF is password-protected or invalid,
        // handleLoadingFailed() or handlePasswordRequested() will be called.
        // We keep loading state true until one of those handlers is called.
      } catch (error) {
        // Handle any errors during fetch or processing
        console.error('PDF Fetch Error:', {
          message: error.message,
          name: error.name,
          url: this.src,
          errorObject: error,
        })

        // Provide specific guidance based on error type
        if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
          console.error('🌐 Network Error: Unable to fetch PDF. Check URL and CORS settings.')
        } else if (error.message?.includes('404')) {
          console.error('🔍 Not Found: PDF file not found at the specified URL')
        } else if (error.message?.includes('403') || error.message?.includes('401')) {
          console.error('🔐 Access Denied: Authentication or authorization required')
        } else {
          console.error('⚠️ Fetch Error: Error occurred while fetching or processing PDF')
        }

        this.error = error.message || 'Failed to load PDF'
        this.loading = false
        this.$emit('error', error)
      }
    },

    setupIntersectionObserver() {
      // Disconnect existing observer if any
      if (this.observer) {
        this.observer.disconnect()
      }

      // Create new IntersectionObserver
      this.observer = new IntersectionObserver(
        (entries) => {
          // entries: Array of observed elements (we only observe one: loadMoreTrigger)
          entries.forEach((entry) => {
            // entry.isIntersecting: true when element is visible (within rootMargin)
            if (entry.isIntersecting && !this.isLoadingMore && this.hasMorePages) {
              this.loadMorePages()
            }
          })
        },
        {
          // Trigger 300px before element enters viewport
          rootMargin: '300px',
          // Trigger as soon as element starts becoming visible
          threshold: 0,
        }
      )

      // Start observing the trigger element
      if (this.$refs.loadMoreTrigger) {
        this.observer.observe(this.$refs.loadMoreTrigger)
      }
    },

    /**
     * LOAD MORE PAGES
     * ---------------
     * Incrementally loads more PDF pages as user scrolls
     *
     * BATCH SIZE:
     * Currently loads 3 pages per batch (Math.min(3, ...))
     *
     * TIMEOUT EXPLAINED:
     * 100ms delay prevents rapid consecutive loads if user scrolls fast
     * Gives browser time to render previous batch before loading next
     *
     * PROCESS:
     * 1. Check if already loading or no more pages
     * 2. Set isLoadingMore flag to prevent concurrent loads
     * 3. Calculate how many pages to load (max 3)
     * 4. Update renderedPages count
     * 5. Vue reactivity triggers re-render of pagesToRender
     * 6. vue-pdf-embed detects new pages and renders them
     * 7. Re-setup observer to watch for next scroll trigger
     *
     * DEBUG:
     * - If pages load too quickly (performance issues), increase timeout
     * - If pages load too slowly (user sees blank space), decrease timeout or increase batch
     * - Monitor renderedPages in Vue DevTools to see loading progress
     */
    loadMorePages() {
      // Guard: Don't load if already loading or no more pages
      if (this.isLoadingMore || !this.hasMorePages) {
        return
      }

      // Set loading flag
      this.isLoadingMore = true

      // Delay to prevent rapid consecutive loads
      setTimeout(() => {
        // Calculate pages to load (max 3, or remaining pages if less than 3)
        const toLoad = Math.min(3, this.numPages - this.renderedPages)

        // Increment rendered pages count
        // This triggers pagesToRender computed property update
        // Which triggers vue-pdf-embed to render new pages
        this.renderedPages += toLoad

        // Reset loading flag
        this.isLoadingMore = false

        // Wait for DOM update, then re-observe trigger
        this.$nextTick(() => {
          // Only re-observe if more pages remain
          if (this.hasMorePages && this.$refs.loadMoreTrigger) {
            this.observer.observe(this.$refs.loadMoreTrigger)
          }
        })
      }, 100)
    },
  },
}
</script>

<style scoped>
#pdf-viewer {
  min-height: 100vh;
}
</style>
